import React, { useState } from 'react';
import { x, styled } from '@xstyled/styled-components';
import PlusIcon from '~/assets/icons/plus.svg';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { ADD_CARD_PLACEHOLDER } from '../itemIds';
import FlipCardEditModal from '../flip-card/FlipCardEditModal';
import emptyImg from '~/training/sequence/templates/items/doorCard/empty.png';
import { useEditorStore } from '~/shell/Store';

interface Props {
  width?: string;
  height?: string;
  text?: string;
  separateTitleAndBody?: boolean;
}

const Outline = styled.div<{ width: string; height: string }>`
  width: ${p => p.width};
  height: ${p => p.height};
  min-width: 200px;
  min-height: 160px;
  border: 2px dashed #9738ff;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #4a4a55;
  font-weight: 600;
  font-size: 16px;
  gap: 8px;
  text-align: center;
`;

export type AddCardPlaceholderDefinition = ItemDefinition<
  typeof ADD_CARD_PLACEHOLDER,
  Props
>;

export class AddCardPlaceholder extends Item<
  typeof ADD_CARD_PLACEHOLDER,
  Props
> {
  protected render({
    width = '295.67px',
    height = '270px',
    text = 'Add a card',
    separateTitleAndBody = false
  }: RenderProps<Props>) {
    const { sequence, contentEditable } = useEditorStore();
    const [creatingId, setCreatingId] = useState<string | number | null>(null);
    const existingCardCount = (sequence?.items || []).filter(
      (it: any) => it?.__typename === 'Card'
    ).length;
    const limitReached = existingCardCount >= 9;

    const handleAdd = () => {
      if (!contentEditable || !sequence || limitReached) return;
      // Create a temporary optimistic card subitem with a negative id (local) until user submits
      const tempId = `temp-${Date.now()}`;
      setCreatingId(tempId);
    };

    return (
      <>
        {!limitReached && (
          <Outline
            width={width}
            height={height}
            role="button"
            aria-label={text}
            tabIndex={0}
            onClick={handleAdd}
          >
            <PlusIcon width={18} height={18} />
            <x.span>{text}</x.span>
          </Outline>
        )}
        {creatingId && (
          <FlipCardEditModal
            open={true}
            onClose={() => setCreatingId(null)}
            cardId={creatingId}
            isNew
            defaultImageUrl={emptyImg}
            separateTitleAndBody={separateTitleAndBody}
          />
        )}
      </>
    );
  }
}

registry[ADD_CARD_PLACEHOLDER] = AddCardPlaceholder;
